import { useClient } from '../context/ClientContext';

// The tenant's language, from `locale` in /api/config (its first configured
// language), read the way the API reads it: by primary subtag. Never the
// visitor's browser.
export function speaksPortuguese(locale) {
  if (typeof locale !== 'string') return false;
  return locale.trim().toLowerCase().replace('_', '-').split('-')[0] === 'pt';
}

// What a visitor of a Portuguese-speaking tenant reads in place of the app's
// English, keyed by that English so a call site still reads as the sentence it
// shows. Written 2026-09-26 to NPAS's rules: speak as "nós", and never say that
// we searched or consulted sources. Server-side copy lives in
// concierge-api/agents/concierge/response_copy.py.
export const PORTUGUESE = {
  'This link has an invalid reference code. Please ask the team for a new link.':
    'Este link tem um código inválido. Peça um novo link à equipe.',
  // Conversation chrome
  Ask: 'Enviar',
  'Send message': 'Enviar mensagem',
  'Continue on WhatsApp': 'Continuar no WhatsApp',
  'This conversation continues on WhatsApp.': 'Esta conversa continua pelo WhatsApp.',
  'Could not open WhatsApp now. Please try again.':
    'Não foi possível abrir o WhatsApp agora. Tente de novo.',
  // "Nova conversa" while a person is involved (docs/plans/2026-10-12-web-chat-refresh, D4)
  // True whether or not someone claimed the case yet (NPAS NOVA1, 2026-10-15).
  'The team has been told about this conversation. If you start a new one, this conversation will end.':
    'A equipe já foi avisada sobre esta conversa. Se começar uma nova, este atendimento será encerrado.',
  // A case closed while the visitor waited (D7 of docs/plans/2026-10-15-npas-open-issues).
  'This request was closed. If you need anything else, just write here.':
    'Este atendimento foi encerrado. Se precisar de mais alguma coisa, é só escrever aqui.',
  'End and start new': 'Encerrar e começar nova',
  'Continue here': 'Continuar aqui',
  'Could not end the conversation now. Please try again.':
    'Não foi possível encerrar agora. Tente de novo.',
  // The web handover form (docs/plans/2026-10-11-web-handover-form, §4 and Task 3)
  'So the team can get back to you, leave a contact:':
    'Para a equipe falar com você, deixe um contato:',
  'About:': 'Sobre:',
  edit: 'editar',
  'Add a subject': 'Adicionar assunto',
  'What is it about?': 'Sobre o que é?',
  Subject: 'Assunto',
  'WhatsApp or e-mail': 'WhatsApp ou e-mail',
  'Your name (optional)': 'Seu nome (opcional)',
  'or leave a contact': 'ou deixe um contato',
  'Call the team': 'Chamar a equipe',
  "I'd rather wait here without leaving a contact": 'Prefiro esperar aqui sem deixar contato',
  'We will use your contact only to reply to this request.':
    'Usaremos seu contato somente para responder a este atendimento.',
  'Check the e-mail': 'Confira o e-mail',
  'Check the number, with the area code': 'Confira o número, com DDD',
  // Under a number typed without +: the tenant's country code is added (Task 10).
  'We will use': 'Vamos usar',
  'Another country? Start with + and the code.': 'De outro país? Comece com + e o código.',
  'Leave a WhatsApp or e-mail, or choose to wait here.':
    'Deixe um WhatsApp ou e-mail, ou escolha esperar aqui.',
  'Could not send it now. Please try again.': 'Não foi possível enviar agora. Tente de novo.',
  'Done! We will reply by': 'Pronto! Vamos responder por',
  'e-mail': 'e-mail',
  'All right, the team will reply here in this chat.':
    'Tudo bem, a equipe vai responder aqui neste chat.',
  'Keep this page open to see the reply.': 'Deixe esta página aberta para ver a resposta.',
  'New conversation': 'Nova conversa',
  'Return to home': 'Voltar ao início',
  'You said': 'Você disse',
  Retry: 'Tentar novamente',
  'See Also': 'Veja também',
  '⚡ Instant Answer': '⚡ Resposta imediata',
  'I hit a streaming error before the answer fully completed. Please try again.':
    'Tivemos um problema e a resposta não foi concluída. Por favor, tente novamente.',
  'This is taking longer than usual and I could not finish the answer. Please send your message again.':
    'A resposta está demorando mais do que o normal e não consegui concluí-la. Por favor, envie sua mensagem novamente.',
  Reference: 'Código',
  'I hit a streaming error and could not finish that answer. Please try again.':
    'Tivemos um problema e não conseguimos concluir a resposta. Por favor, tente novamente.',

  // Source card tooltip
  Official: 'Oficial',
  Verified: 'Verificado',
  Updated: 'Atualizado em',

  // Home
  Spotlight: 'Destaque',

  // Suggestions while typing
  'Suggested answers': 'Respostas sugeridas',
  'Searching...': 'Um instante...',
  "Not what you're looking for?": 'Não é o que você procura?',
  'Esc to dismiss': 'Esc para fechar',

  // Feedback on an answer
  'Good response': 'Resposta útil',
  'Needs improvement': 'Precisa melhorar',
  'Thank you — we’ll look into this.': 'Obrigado — vamos verificar.',
  'Sorry this wasn’t helpful. Could you tell us what went wrong? Our team reviews every piece of feedback to improve answers.':
    'Que pena que não ajudou. Pode nos contar o que faltou? Nossa equipe lê cada comentário para melhorar as respostas.',
  'What was wrong or missing?': 'O que estava errado ou faltando?',
  'I’d like staff to follow up with me once this is addressed':
    'Quero que a equipe entre em contato comigo quando isso for resolvido',
  'Your email address': 'Seu e-mail',
  Skip: 'Pular',
  'Sending...': 'Enviando...',
  'Send feedback': 'Enviar comentário',

  // Source cards under an answer
  'Page no longer available': 'Página não disponível',
  'Loading preview...': 'Carregando prévia...',
  Document: 'Documento',
  Video: 'Vídeo',
  Article: 'Artigo',
  'Video thumbnail': 'Miniatura do vídeo',
  'PDF Document': 'Documento PDF',
  'Article preview': 'Prévia do artigo',
};

// Throws on a missing string rather than showing the English: a Portuguese
// visitor silently reading English is the defect this exists to remove, and
// src/lib/__tests__/copy.test.js fails the build before any such key ships.
export function copyFor(locale) {
  if (!speaksPortuguese(locale)) return (english) => english;
  return (english) => {
    if (!(english in PORTUGUESE)) {
      throw new Error(`No Portuguese copy for "${english}"`);
    }
    return PORTUGUESE[english];
  };
}

export function useCopy() {
  const { clientConfig } = useClient();
  return copyFor(clientConfig?.locale);
}
